import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Search, Edit, Save, Close, Person, Email, Lock, Phone, EventNote } from "@mui/icons-material";

import HttpClient from "../../../../helpers/HttpClient";
import OverlayLoader from "../../../../components/OverlayLoader";
import { showToast } from "../../../../utils/toast";
import { formatDate, validateEmail } from "../../../../utils/generals";
import { validarNickname } from "../../../../utils/nicknameValidator";
import { useAdminModulePermission } from "../../../../contexts/AdminPermissionContext";

interface Usuario {
  id: string;
  nombre_completo: string;
  documento: string;
  email: string;
  nickname: string;
  fecha_nacimiento: string;
  password?: string;
  celular?: string;
  activo?: number;
}

export default function UserEditPage() {
  const { isReadOnly } = useAdminModulePermission();
  const [searchInput, setSearchInput] = useState("");
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<Usuario | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [phoneError, setPhoneError] = useState(false);

  const validarTelefonoInternacional = (phone: string): boolean => {
    const phoneRegex = /^\+\d{10,15}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const asegurarMasEnCelular = (celular: string): string => {
    if (!celular) return '';
    // Convertir a string siempre y si no tiene +, agrégalo
    const celularStr = String(celular).trim();
    if (!celularStr.startsWith('+')) {
      return '+' + celularStr;
    }
    return celularStr;
  };

  const buscarUsuario = async () => {
    if (!searchInput.trim()) {
      showToast("Por favor ingresa un correo o nombre de usuario", "warning");
      return;
    }

    setLoading(true);
    try {
      const response: any = await HttpClient().get(`/admin/user/${searchInput.trim()}`);

      if (response.error === true) {
        setUsuario(null);
        setFormData(null);
        showToast(response.data?.error?.message || "Usuario no encontrado", "error");
      } else if (response.data && response.data.email) {
        // Asegurar que el celular tenga + si existe
        const userData = { ...response.data };
        if (userData.celular) {
          userData.celular = asegurarMasEnCelular(userData.celular);
        }
        setUsuario(userData);
        setFormData({ ...userData });
        setEditMode(false);
        showToast("Usuario encontrado", "success");
      } else {
        setUsuario(null);
        setFormData(null);
        showToast("No se pudo obtener la información del usuario", "error");
      }
    } catch (error: any) {
      console.error(error);
      setUsuario(null);
      setFormData(null);
      showToast(error.message || "Error al buscar el usuario", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;
    
    // Validar formato internacional de teléfono para campo celular
    if (name === "celular") {
      // Permite + seguido de dígitos (formato internacional)
      value = value.replace(/[^\d+]/g, "");
      // Si no comienza con +, agregarlo automáticamente si está vacío
      if (value && !value.startsWith("+")) {
        value = "+" + value;
      }
      // Limitar a máximo 15 dígitos (estándar E.164) + el símbolo +
      if (value.startsWith("+")) {
        const digits = value.slice(1);
        if (digits.length > 15) {
          value = "+" + digits.slice(0, 15);
        }
      }
      // Validar en tiempo real
      if (value && !validarTelefonoInternacional(value)) {
        setPhoneError(true);
      } else {
        setPhoneError(false);
      }
    }
    
    if (formData) {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      if (formData) {
        setFormData({
          ...formData,
          fecha_nacimiento: formatDate(date),
        });
      }
    }
  };

  const validarFormulario = (): boolean => {
    if (!formData) return false;

    if (!formData.nombre_completo?.trim()) {
      showToast("Nombre completo es requerido");
      return false;
    }
    if (!String(formData.documento).trim()) {
      showToast("Documento es requerido");
      return false;
    }
    if (!formData.email?.trim()) {
      showToast("Email es requerido");
      return false;
    }
    if (!validateEmail(formData.email)) {
      showToast("Email inválido");
      return false;
    }
    if (!String(formData.nickname).trim()) {
      showToast("Nombre de usuario es requerido");
      return false;
    }
    const validacionNickname = validarNickname(String(formData.nickname));
    if (!validacionNickname.valido) {
      showToast(validacionNickname.error || "Nombre de usuario inválido");
      return false;
    }
    if (!formData.fecha_nacimiento?.trim()) {
      showToast("Fecha de nacimiento es requerida");
      return false;
    }
    // Validar celular si está presente
    if (formData.celular && !validarTelefonoInternacional(formData.celular)) {
      showToast("Formato de celular inválido. Debe ser: +57 3001234567 (10-15 dígitos después del +)");
      setPhoneError(true);
      return false;
    }
    // Celular y contraseña son opcionales - no validar

    return true;
  };

  const guardarCambios = async () => {
    if (!validarFormulario() || !usuario) return;

    setLoading(true);
    try {
      const dataToSend: any = {
        nombre_completo: formData!.nombre_completo,
        documento: formData!.documento,
        email: formData!.email,
        nickname: formData!.nickname,
        fecha_nacimiento: formData!.fecha_nacimiento,
        celular: formData!.celular ? (formData!.celular.startsWith("+") ? formData!.celular : "+" + formData!.celular) : "",
        activo: formData!.activo === 1 ? 1 : 0,
      };

      if (formData!.password && formData!.password.trim()) {
        dataToSend.password = formData!.password;
      }

      const response: any = await HttpClient().put(`/admin/user/edit/${usuario.email}`, dataToSend);

      if (response.error === true) {
        showToast(response.data?.error?.message || "Error al actualizar el usuario", "error");
      } else {
        showToast("Usuario actualizado exitosamente", "success");
        setUsuario({ ...usuario, ...dataToSend });
        setFormData({ ...usuario, ...dataToSend });
        setEditMode(false);
        setDialogOpen(false);
      }
    } catch (error: any) {
      console.error(error);
      showToast(error.message || "Error al actualizar el usuario", "error");
    } finally {
      setLoading(false);
    }
  };

  const limpiarBusqueda = () => {
    setSearchInput("");
    setUsuario(null);
    setFormData(null);
    setEditMode(false);
  };

  return (
    <Box sx={{ backgroundColor: "#F5F5F5", minHeight: "100vh", p: 2 }}>
      <OverlayLoader open={loading} ready={!loading} buttonClick={() => {}} />

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          fontWeight="bold"
          color="#374151"
          sx={{ fontSize: "1.3rem" }}
        >
          Editar Usuario
        </Typography>
      </Box>

      {/* Tarjeta de búsqueda */}
      <Card
        sx={{
          borderRadius: 2,
          border: "1px solid #E5E7EB",
          backgroundColor: "white",
          mb: 3,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Search sx={{ color: "#6B7280", fontSize: 16 }} />
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ fontSize: "0.85rem" }}
                >
                  Buscar por Correo o Nombre de Usuario:
                </Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder="usuario@ejemplo.com o usuario123"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    buscarUsuario();
                  }
                }}
                disabled={loading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#F9FAFB",
                    fontSize: "0.8rem",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", height: "100%" }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<Search />}
                  onClick={buscarUsuario}
                  disabled={loading}
                  size="small"
                  sx={{ height: "40px" }}
                >
                  {loading ? <CircularProgress size={20} /> : "Buscar"}
                </Button>
                {usuario && (
                  <Button
                    variant="outlined"
                    startIcon={<Close />}
                    onClick={limpiarBusqueda}
                    size="small"
                    sx={{ height: "40px" }}
                  >
                    Limpiar
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Formulario de edición */}
      {usuario && formData && (
        <Card sx={{ borderRadius: 2, border: "1px solid #E5E7EB", backgroundColor: "white" }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Datos del Usuario
              </Typography>
              {!isReadOnly && !editMode ? (
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  onClick={() => setEditMode(true)}
                  color="primary"
                  size="small"
                >
                  Editar
                </Button>
              ) : !isReadOnly && editMode ? (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={() => setDialogOpen(true)}
                    color="success"
                    disabled={loading}
                    size="small"
                  >
                    {loading ? <CircularProgress size={20} /> : "Guardar"}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Close />}
                    onClick={() => {
                      setEditMode(false);
                      setFormData({ ...usuario });
                    }}
                    size="small"
                  >
                    Cancelar
                  </Button>
                </Box>
              ) : null}
            </Box>

            {/* Alertas de advertencia */}
            {editMode && (
              <>
                <Alert severity="warning" sx={{ mb: 2, fontSize: "0.8rem" }}>
                  <Typography variant="caption" fontWeight="bold" display="block">
                    Advertencia: Correo Electrónico
                  </Typography>
                  <Typography variant="caption">
                    Es único y se utiliza como identificador. Cambiar este valor puede afectar el acceso del usuario.
                  </Typography>
                </Alert>
                <Alert severity="warning" sx={{ mb: 3, fontSize: "0.8rem" }}>
                  <Typography variant="caption" fontWeight="bold" display="block">
                    Advertencia: Nombre de Usuario
                  </Typography>
                  <Typography variant="caption">
                    Es único y se utiliza como identificador. Cambiar este valor puede afectar el acceso del usuario.
                  </Typography>
                </Alert>
              </>
            )}

            <Grid container spacing={3}>
              {/* Nombre Completo */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Person sx={{ color: "#6B7280", fontSize: 16 }} />
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.85rem" }}>
                    Nombre Completo:
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  name="nombre_completo"
                  value={formData.nombre_completo || ""}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: editMode ? "#FFFFFF" : "#F9FAFB",
                      fontSize: "0.8rem",
                    },
                  }}
                />
              </Grid>

              {/* Documento */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Person sx={{ color: "#6B7280", fontSize: 16 }} />
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.85rem" }}>
                    Documento:
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  name="documento"
                  value={formData.documento || ""}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: editMode ? "#FFFFFF" : "#F9FAFB",
                      fontSize: "0.8rem",
                    },
                  }}
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Email sx={{ color: "#6B7280", fontSize: 16 }} />
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.85rem" }}>
                    Correo Electrónico:
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  name="email"
                  value={formData.email || ""}
                  onChange={handleInputChange}
                  disabled
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#F9FAFB",
                      fontSize: "0.8rem",
                    },
                  }}
                  helperText="Identificador único (no editable)"
                />
              </Grid>

              {/* Nickname */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Person sx={{ color: "#6B7280", fontSize: 16 }} />
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.85rem" }}>
                    Nombre de Usuario:
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  name="nickname"
                  value={formData.nickname || ""}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  inputProps={{ maxLength: 50 }}
                  error={!!(
                    editMode &&
                    formData.nickname &&
                    !validarNickname(String(formData.nickname)).valido
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: editMode ? "#FFFFFF" : "#F9FAFB",
                      fontSize: "0.8rem",
                    },
                  }}
                  helperText={
                    !editMode
                      ? "Identificador único"
                      : formData.nickname
                      ? validarNickname(String(formData.nickname)).error || "Nombre de usuario válido"
                      : "Entre 7 y 50 caracteres. Permitidos: a-z, 0-9, . _ -"
                  }
                />
              </Grid>

              {/* Fecha de Nacimiento */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <EventNote sx={{ color: "#6B7280", fontSize: 16 }} />
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.85rem" }}>
                    Fecha de Nacimiento:
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={formData.fecha_nacimiento || ""}
                  onChange={(e) => {
                    if (formData) {
                      setFormData({
                        ...formData,
                        fecha_nacimiento: e.target.value,
                      });
                    }
                  }}
                  disabled={!editMode}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: editMode ? "#FFFFFF" : "#F9FAFB",
                      fontSize: "0.8rem",
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              {/* Celular/WhatsApp */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Phone sx={{ color: "#6B7280", fontSize: 16 }} />
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.85rem" }}>
                    Celular/WhatsApp (opcional):
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  name="celular"
                  placeholder="+57 (código país) número"
                  value={formData.celular || ""}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  error={phoneError}
                  helperText={phoneError ? "Formato inválido: +57 3001234567 (10-15 dígitos)" : "Formato internacional: +57 3001234567"}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: editMode ? "#FFFFFF" : "#F9FAFB",
                      fontSize: "0.8rem",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.activo === 1}
                      onChange={(e) => {
                        if (formData) {
                          setFormData({
                            ...formData,
                            activo: e.target.checked ? 1 : 0,
                          });
                        }
                      }}
                      disabled={!editMode}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.85rem" }}>
                        Estado: {formData.activo === 1 ? "Activo" : "Inactivo"}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              {/* Contraseña */}
              {editMode && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 1, fontSize: "0.75rem" }}>
                    <Typography variant="caption">
                      Déjalo vacío si no deseas cambiar la contraseña
                    </Typography>
                  </Alert>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Lock sx={{ color: "#6B7280", fontSize: 16 }} />
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.85rem" }}>
                      Nueva Contraseña (opcional):
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    name="password"
                    value={formData.password || ""}
                    onChange={handleInputChange}
                    type="password"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#FFFFFF",
                        fontSize: "0.8rem",
                      },
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Dialog de confirmación */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Confirmar cambios</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 2 }}>
            ¿Estás seguro de que deseas guardar los cambios? Asegúrate de que el correo y nombre de usuario sean correctos, ya que son
            identificadores únicos.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button onClick={guardarCambios} variant="contained" color="success">
            Guardar cambios
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
